import React, { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, useLocation } from "react-router-dom"
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Table,
  Badge,
  Button,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap"
import Breadcrumbs from "components/Common/Breadcrumb"
import { useTranslation } from "react-i18next"
import { showAlert } from "store/reducers/alertReducer"
import isPermissionEnabled from "common/isPermissionEnabled"
import TableShimmer from "components/Shared/TableShimmer"
import DataExportDropdown from "components/Shared/DataExportDropdown"
import Filters from "components/Common/Filters"
import Paginations from "components/Common/Paginations"
import DeleteModal from "components/Common/DeleteModal"
import {
  getAllGiftCardCategories,
  deleteGiftCardCategory,
  updateGiftCardCategory,
} from "store/actions/giftCardCategoriesAction"
import formatDateTime from "components/Shared/formatDateTime"
import { filters } from "components/Common/constants"
import Switch from "components/Shared/Switch"

const GiftCardCategories = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t("Gift Card Categories ")

  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const pageNumber = parseInt(searchParams.get("page")) || 1

  const {
    loading,
    isLoading,
    totalCount,
    totalPages,
    currentPage = 1,
    itemsPerPage = 10,
    categories,
  } = useSelector(state => state.GiftCardCategories)

  const { userRole, accountType } = useSelector(state => state.Login)
  const [usersPermissions, setUsersPermissions] = useState({
    list: null,
    create: null,
    edit: null,
    delete: null,
  })

  // FIRST CHECK PERMISSION, IF DOES'T HAVE THEN REDIRECT TO DASHBOARD
  useEffect(() => {
    const isPermissionList = isPermissionEnabled(
      userRole,
      37,
      "gift_card_1",
      accountType
    )
    const isPermissionCreate = isPermissionEnabled(
      userRole,
      37,
      "gift_card_2",
      accountType
    )
    const isPermissionEdit = isPermissionEnabled(
      userRole,
      37,
      "gift_card_3",
      accountType
    )
    const isPermissionDelete = isPermissionEnabled(
      userRole,
      37,
      "gift_card_4",
      accountType
    )
    if (!isPermissionList) {
      navigate("/dashboard")
    } else {
      fetchAllRecords()
    }
    setUsersPermissions({
      list: isPermissionList,
      create: isPermissionCreate,
      edit: isPermissionEdit,
      delete: isPermissionDelete,
    })
  }, [userRole])

  const [selectedId, setSelectedId] = useState(null)
  const [isApiCalled, setIsApiCalled] = useState(false)
  const [itemIdToDelete, setItemIdToDelete] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const flatpickrRef = useRef(null)

  const [dateRange, setDateRange] = useState({})
  const [selectedFilters, setSelectedFilters] = useState({
    status: "",
    sortOrder: 1,
  })

  const handleSearchChange = e => {
    setSearchTerm(e.target.value)
  }
  const handleFilterChange = (selectedValue, filterName) => {
    setSelectedFilters(prevFilters => ({
      ...prevFilters,
      [filterName]: Number(selectedValue),
    }))
  }

  const fetchAllRecords = () => {
    const payload = {
      perPage: itemsPerPage,
      pageNo: pageNumber,
      active: null,
      orderBy: 1,
      keyword: "",
      dateRange:
        dateRange.min && dateRange.max
          ? { min: dateRange.min, max: dateRange.max }
          : {},
    }
    dispatch(getAllGiftCardCategories(payload))
    setIsApiCalled(true)
  }

  const getApiCall = (page = 1) => {
    const { status, sortOrder } = selectedFilters
    const payload = {
      perPage: itemsPerPage,
      pageNo: page,
      active:
        status === 2 || status === "" ? null : status === 1 ? true : false,
      orderBy: Number(sortOrder),
      keyword: searchTerm || "",
      dateRange:
        dateRange.min && dateRange.max
          ? { min: dateRange.min, max: dateRange.max }
          : {},
    }

    return dispatch(getAllGiftCardCategories(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          navigate(`/giftcard-categories?page=${page}`)
        }
      })
      .catch(error => {
        console.error(error)
      })
  }

  const handleSearch = () => {
    getApiCall()
  }

  const onPageChange = page => {
    getApiCall(page)
  }

  const handleReset = () => {
    setSearchTerm("")
    const payload = {
      perPage: itemsPerPage,
      pageNo: 1,
      active: null,
      orderBy: 1,
      keyword: "",
      dateRange: {},
    }
    dispatch(getAllGiftCardCategories(payload))
    navigate(`/giftcard-categories?page=${1}`)
    setSelectedFilters({
      status: "",
      sortOrder: 1,
    })
    setDateRange({})
    if (flatpickrRef.current) {
      flatpickrRef.current.flatpickr.clear()
    }
  }

  const handleDelete = () => {
    setShowDeleteModal(false)
    const payload = {
      id: itemIdToDelete,
    }
    dispatch(deleteGiftCardCategory(payload)).then(() => {
      dispatch(
        showAlert({ text: "Category deleted successfully", type: "success" })
      )
      dispatch(
        getAllGiftCardCategories({
          perPage: itemsPerPage,
          pageNo: currentPage,
          active:
            selectedFilters.status === 2 || selectedFilters.status === ""
              ? null
              : selectedFilters.status === 1
              ? true
              : false,
          orderBy: selectedFilters.sortOrder,
          keyword: searchTerm || "",
          dateRange:
            dateRange.min && dateRange.max
              ? { min: dateRange.min, max: dateRange.max }
              : {},
        })
      )
    })
  }

  const handleStatusChange = async category => {
    try {
      const payload = {
        id: category._id,
        cardName: category.cardName,
        cardNameAr: category.cardNameAr,
        cardDescription: category.cardDescription,
        cardDescriptionAr: category.cardDescriptionAr,
        cardType: category.cardType,
        active: !category.active,
      }
      const response = await dispatch(updateGiftCardCategory(payload))
      if (response.payload.statusCode === 200) {
        dispatch(
          showAlert({ text: "Status updated successfully", type: "success" })
        )
        // Refresh list with current filters
        const { status, sortOrder } = selectedFilters
        const refreshPayload = {
          perPage: itemsPerPage,
          pageNo: currentPage,
          active: status === 2 ? null : status === 1 ? true : false,
          orderBy: Number(sortOrder),
          keyword: searchTerm || "",
          dateRange:
            dateRange.min && dateRange.max
              ? { min: dateRange.min, max: dateRange.max }
              : {},
        }
        dispatch(getAllGiftCardCategories(refreshPayload))
      }
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs />
        <Row>
          <Col xl={12}>
            <Card>
              <DataExportDropdown
                cardTitle={t("Gift Card Categories")}
                isAddBtn={loading ? false : usersPermissions.create}
                addBtnText={t("Add Category")}
                onAddBtnClick={() => navigate("/giftcard-categories/add")}
              />
              <CardBody>
                <Filters
                  filters={filters}
                  searchTerm={searchTerm}
                  handleSearchChange={handleSearchChange}
                  handleFilterChange={handleFilterChange}
                  handleSearch={handleSearch}
                  onReset={handleReset}
                  selectedFilters={selectedFilters}
                  searchBy={t("Name")}
                  colSize={4}
                  isRangeDate={true}
                  dateRange={dateRange}
                  setDateRange={setDateRange}
                  flatpickrRef={flatpickrRef}
                />
                <div className="">
                  {loading ? (
                    <TableShimmer row={7} col={5} />
                  ) : categories.length === 0 ? (
                    <p className="text-center my-3">{t("No data found")}</p>
                  ) : (
                    <Table className="table mb-0" hover>
                      <thead className="table-light full_th">
                        <tr>
                          <th style={{ maxWidth: 50 }}>{t("S. No.")}</th>
                          <th>{t("Name")}</th>
                          <th>{t("Type")}</th>
                          <th>{t("Description")}</th>
                          <th>{t("Active/Inactive")}</th>
                          <th className="text-end">
                            <span
                              className="d-inline-block "
                              style={{
                                paddingRight: 30,
                                display: "block",
                              }}
                            >
                              {t("Action")}
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {categories.map((item, index) => {
                          const serialNumber =
                            ((currentPage || 1) - 1) * (itemsPerPage || 10) +
                            index +
                            1
                          return (
                            <tr
                              key={item._id}
                              onClick={() => {
                                navigate(
                                  `/giftcard-categories/details?cardType=${item.cardType}`
                                )
                              }}
                              className="cursor-pointer"
                            >
                              <th scope="row">{serialNumber}</th>
                              <td>
                                {language === "sa"
                                  ? item.cardNameAr
                                  : item.cardName || "-"}
                              </td>
                              <td>{item.cardType || "-"}</td>
                              <td>
                                {language === "sa"
                                  ? item.cardDescriptionAr
                                  : item.cardDescription || "-"}
                              </td>
                              <td>
                                {item.active ? (
                                  <Badge className="bg-success">
                                    {t("Active")}
                                  </Badge>
                                ) : (
                                  <Badge className="bg-danger">
                                    {t("Inactive")}
                                  </Badge>
                                )}
                              </td>
                              <td>
                                <div className="d-flex align-items-center justify-content-end gap-2">
                                  {usersPermissions.edit && (
                                    <Switch
                                      onClickHandler={e => {
                                        e.stopPropagation()
                                        handleStatusChange(item)
                                      }}
                                      isChecked={item.active}
                                      disabled={
                                        item._id === selectedId && isLoading
                                      }
                                    />
                                  )}
                                  <UncontrolledDropdown>
                                    <DropdownToggle
                                      tag="a"
                                      className="card-drop"
                                      onClick={e => e.stopPropagation()}
                                    >
                                      <i className="mdi mdi-dots-vertical font-size-18"></i>
                                    </DropdownToggle>
                                    <DropdownMenu className="dropdown-menu-end">
                                      {usersPermissions.edit && (
                                        <DropdownItem
                                          onClick={e => {
                                            e.stopPropagation()
                                            navigate(
                                              `/giftcard-categories/edit?cardType=${item.cardType}`
                                            )
                                          }}
                                        >
                                          <i className="mdi mdi-pencil font-size-16 text-success me-1"></i>
                                          {t("Edit")}
                                        </DropdownItem>
                                      )}
                                      <DropdownItem
                                        onClick={e => {
                                          e.stopPropagation()
                                          navigate(
                                            `/giftcard-categories/details?cardType=${item.cardType}`
                                          )
                                        }}
                                      >
                                        <i className="mdi mdi-eye font-size-16 text-primary me-1"></i>
                                        {t("View")}
                                      </DropdownItem>
                                      {usersPermissions.delete && (
                                        <DropdownItem
                                          onClick={e => {
                                            e.stopPropagation()
                                            setItemIdToDelete(item._id)
                                            setShowDeleteModal(true)
                                          }}
                                        >
                                          <i className="mdi mdi-trash-can font-size-16 text-danger me-1"></i>
                                          {t("Delete")}
                                        </DropdownItem>
                                      )}
                                    </DropdownMenu>
                                  </UncontrolledDropdown>
                                </div>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </Table>
                  )}
                </div>
                {!loading && categories.length > 0 && (
                  <div className="mt-3">
                    <Paginations
                      data={categories}
                      itemsPerPage={itemsPerPage}
                      onPageChange={onPageChange}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalCount={totalCount}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      <DeleteModal
        show={showDeleteModal}
        isLoading={isLoading}
        onDeleteClick={handleDelete}
        onCloseClick={() => {
          setShowDeleteModal(false)
          setItemIdToDelete(null)
        }}
        buttonText={t("Delete")}
        bodyTitle={t("Are you sure you want to delete this {{name}}?", {
          name: t("Category"),
        })}
      />
    </div>
  )
}

export default GiftCardCategories
